import { defineStore } from 'pinia';
import { ref } from 'vue';

import { openExternalLink } from '@/lib/commonFunctions';
import {
    createPortalSession,
    createUpgradeProSession
} from '@/services/billing';

import { useAccessContextStore } from './accessContextStore';
import { useTenantStore } from './tenant';

function getCurrentReturnPath(): string {
    return window.location.pathname + window.location.search;
}

export const useStripeStore = defineStore('stripe', () => {
    const tenantStore = useTenantStore();
    const isPortalLoading = ref(false);
    const isUpgradeLoading = ref(false);

    // The Stripe portal/checkout opens in a new tab. While the user is on it,
    // the original tab is hidden. Subscription state can change server-side
    // (e.g. trial ended via the customer.updated webhook after a payment
    // method update), so we need to invalidate the locally cached access
    // context when the user comes back. Using a single shared listener avoids
    // stacking multiple handlers if the user opens Stripe several times.
    let isWaitingForBillingReturn = false;

    function refreshAccessContextOnReturn() {
        if (isWaitingForBillingReturn) return;
        isWaitingForBillingReturn = true;

        const accessContextStore = useAccessContextStore();
        const handler = () => {
            if (document.visibilityState !== 'visible') return;
            document.removeEventListener('visibilitychange', handler);
            isWaitingForBillingReturn = false;
            accessContextStore.ensureLoaded(true);
        };
        document.addEventListener('visibilitychange', handler);
    }

    async function openStripePortal() {
        if (isPortalLoading.value || !tenantStore.tenantId) return;
        isPortalLoading.value = true;
        try {
            const data = await createPortalSession(
                tenantStore.tenantId,
                getCurrentReturnPath()
            );
            if (data?.url) {
                openExternalLink(data.url);
                refreshAccessContextOnReturn();
            }
        } catch (error) {
            console.error('Failed to create Stripe portal session:', error);
        } finally {
            isPortalLoading.value = false;
        }
    }

    /**
     * Routes the user to the most direct Stripe surface for converting to Pro.
     *
     * Trialing/past_due tenants land on the customer portal's
     * `payment_method_update` flow. Adding a payment method there triggers the
     * `customer.updated` webhook which ends the trial immediately, resetting
     * the quota window and unlocking Pro features without waiting for the
     * original `trial_end`.
     */
    async function openUpgradeFlow() {
        if (isUpgradeLoading.value || !tenantStore.tenantId) return;
        isUpgradeLoading.value = true;
        try {
            const data = await createUpgradeProSession(
                tenantStore.tenantId,
                getCurrentReturnPath()
            );
            if (data?.url) {
                openExternalLink(data.url);
                refreshAccessContextOnReturn();
            }
        } catch (error) {
            console.error('Failed to create Stripe upgrade session:', error);
        } finally {
            isUpgradeLoading.value = false;
        }
    }

    return {
        openStripePortal,
        openUpgradeFlow,
        isPortalLoading,
        isUpgradeLoading
    };
});
